import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { authService } from '../../services/api';
import Copilot from './Copilot';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  @media (max-width: 800px) { flex-direction: column; }
`;

const Sidebar = styled.div`
  width: 250px;
  min-width: 250px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.lg};
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  @media (max-width: 800px) {
    width: 100%; min-width: 0; height: auto; position: static;
    border-right: 0; border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: .75rem 1rem;
  }
`;

const Logo = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  @media (max-width: 800px) { font-size: 1.1rem; margin-bottom: .5rem; padding-bottom: .5rem; }
`;

const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  @media (max-width: 800px) { flex-direction: row; overflow-x: auto; }
`;

const NavItem = styled(Link)<{ $active?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  text-decoration: none;
  color: ${({ theme, $active }) => 
    $active ? theme.colors.primary : theme.colors.text.primary};
  background-color: ${({ theme, $active }) => 
    $active ? theme.colors.background : 'transparent'};
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  transition: all 0.3s ease;
  @media (max-width: 800px) { white-space: nowrap; padding: .6rem .75rem; }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MainContent = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
  min-width: 0; /* This is important to prevent flex items from overflowing */
  overflow-x: hidden;
`;

const Header = styled.header`
  background-color: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem; flex-wrap: wrap;
`;

const HeaderTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.5rem;
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const AuthStatus = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const LogoutButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.light};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const Content = styled.main`
  padding: ${({ theme }) => theme.spacing.lg};
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar autenticação ao carregar
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const isAuthenticated = !!token;
    
    if (!isAuthenticated) {
      const next = `${location.pathname}${location.search}`;
      navigate(`/admin/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    
    setIsAuthenticated(true);
  }, [location.pathname, location.search, navigate]);

  const menuItems = [
    { path: '/admin', label: 'Dashboard', exact: true },
    { path: '/admin/properties', label: 'Propriedades' },
    { path: '/admin/media-batches', label: 'Mídias em Lote' },
    { path: '/admin/property-follow-up', label: 'Acompanhamento' },
    { path: '/admin/categories', label: 'Categorias' },
    { path: '/admin/locais', label: 'Páginas de local' },
    { path: '/admin/editorial', label: 'Blog e aprovação' },
    { path: '/admin/leads', label: 'Clientes' },
    { path: '/admin/brokers', label: 'Corretores' },
    { path: '/admin/integrations/canal-pro', label: 'Canal Pro' },
    { path: '/admin/settings', label: 'Configurações' },
  ];

  const getPageTitle = () => {
    // Verificar se é uma sub-rota de integração (mantida para retrocompatibilidade)
    if (location.pathname.startsWith('/admin/integrations')) {
      return 'Integrações';
    }

    const currentItem = menuItems.find(item => 
      item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
    );
    return currentItem?.label || 'Admin';
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    navigate('/admin/login');
  };

  const token = authService.getToken();
  const tokenStatus = token ? `Token: ${token.substring(0, 15)}...` : 'Não autenticado';

  return (
    <LayoutContainer>
      <Sidebar>
        <Logo>Admin Panel</Logo>
        <NavList>
          {menuItems.map(item => (
            <NavItem
              key={item.path}
              to={item.path}
              $active={item.exact 
                ? location.pathname === item.path 
                : location.pathname.startsWith(item.path)}
            >
              {item.label}
            </NavItem>
          ))}
        </NavList>
      </Sidebar>
      
      <MainContent>
        <Header>
          <HeaderTitle>{getPageTitle()}</HeaderTitle>
          <UserMenu>
            <AuthStatus>{tokenStatus}</AuthStatus>
            <span>Administrador</span>
            <LogoutButton onClick={handleLogout}>
              Sair
            </LogoutButton>
          </UserMenu>
        </Header>
        
        <Content>
          <Outlet />
        </Content>
      </MainContent>
      
      {/* Copilot */}
      <Copilot 
        isOpen={isCopilotOpen} 
        onToggle={() => setIsCopilotOpen(!isCopilotOpen)} 
      />
    </LayoutContainer>
  );
};

export default AdminLayout; 
